import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef } from "react";
import { SnapshotsTable } from "@pages/project-details/project-snapshots/snapshots-table";
import { ColumnPreferencesProvider } from "@src/context-providers/table/column-preferences-provider";
import { DEFAULT_SNAPSHOTS_SORT_BY, SNAPSHOTS_SORT_BY_MAP, SNAPSHOTS_SORT_OPTIONS } from "@pages/project-details/project-snapshots/snapshots-table-constants";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { SNAPSHOTS_TABLE_COLUMNS } from "@pages/project-details/project-snapshots/snapshots-table-types";
import { isSnapshotsSortBy } from "@custom-types/type-guards";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { getSnapshotsSortBy, sortSnapshots } from "@pages/project-details/project-snapshots/snapshots-table-utils";
import { SortSelector } from "@components/common/sort-selector";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { SnapshotEvents } from "@utils/track-event/track-event-list";
import { searchSelector } from "@store/ui/ui-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { SnapshotsEmptyPage } from "@pages/project-details/project-snapshots/snapshots-empty-page";
import { BulkDeleteSnapshotsSuccessDialog } from "@pages/project-details/project-snapshots/bulk-actions/bulk-delete-snapshots-success-dialog";
import { fetchProjectSnapshots } from "@store/projects/projects-slice-thunk";
import { isFetchingProjectSnapshotsSelector, selectedProjectSnapshotsSelector } from "@store/projects/projects-selector";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useSnapshotBulkActions } from "@pages/project-details/project-snapshots/hooks/use-snapshot-bulk-actions";
import { getExportComponent } from "@utils/member-utils";
import { isUserSnapshotAdmin } from "@utils/permission-control/snapshot-permission-control";
import { useToast } from "@flat-ui";
import { useMediaQueryList } from "@hooks/use-media-query";
import { SnapshotsMobileView } from "@pages/project-details/project-snapshots/snapshots-mobile-view";
import { ProjectSnapshotsKpi } from "@pages/project-details/project-snapshots/project-snapshots-kpi";
import { CreateSnapshot } from "@pages/project-details/project-snapshots/create-snapshot";
import { useSnapshotsBeingCreatedTracker } from "@pages/project-details/project-snapshots/hooks/use-snapshots-being-created-tracker";

/** Renders the project snapshots tab */
export function ProjectSnapshots(t0) {
  const $ = _c(40);
  const {
    projectId
  } = t0;
  const hasTrackedEvent = useRef(false);
  const {
    trackEvent
  } = useTrackEvent();
  const dispatch = useAppDispatch();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    openToast
  } = useToast();
  const snapshots = useAppSelector(selectedProjectSnapshotsSelector);
  const isLoading = useAppSelector(isFetchingProjectSnapshotsSelector);
  const {
    debouncedSearchText
  } = useAppSelector(searchSelector);
  let t1;
  if ($[0] !== projectId) {
    t1 = {
      projectId
    };
    $[0] = projectId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useSnapshotsBeingCreatedTracker(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      defaultSort: DEFAULT_SNAPSHOTS_SORT_BY,
      isValidSort: isSnapshotsSortBy
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    sortBy,
    setSortBy
  } = useSubjectsSortByQuery(t2);
  let t3;
  if ($[3] !== debouncedSearchText || $[4] !== snapshots || $[5] !== sortBy) {
    let t4;
    if ($[7] !== debouncedSearchText) {
      t4 = snapshot => snapshot.name.toLowerCase().includes(debouncedSearchText.toLowerCase());
      $[7] = debouncedSearchText;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = sortSnapshots(snapshots, sortBy).filter(t4);
    $[3] = debouncedSearchText;
    $[4] = snapshots;
    $[5] = sortBy;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const sortedFilteredSnapshots = t3;
  let t4;
  if ($[9] !== sortedFilteredSnapshots) {
    t4 = {
      snapshots: sortedFilteredSnapshots
    };
    $[9] = sortedFilteredSnapshots;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const buttonComponents = useSnapshotBulkActions(t4);
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  let t5;
  if ($[11] !== dispatch || $[12] !== handleErrorWithToast || $[13] !== projectId) {
    t5 = () => {
      const fetchSnapshots = async function fetchSnapshots() {
        ;
        try {
          await dispatch(fetchProjectSnapshots({
            projectId
          })).unwrap();
        } catch (t6) {
          const error = t6;
          handleErrorWithToast({
            id: `ProjectSnapshots-fetchSnapshots-${Date.now().toString()}`,
            title: "Could not fetch project snapshots",
            error
          });
        }
      };
      fetchSnapshots();
    };
    $[11] = dispatch;
    $[12] = handleErrorWithToast;
    $[13] = projectId;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== dispatch || $[16] !== handleErrorWithToast || $[17] !== openToast || $[18] !== projectId) {
    t6 = [dispatch, handleErrorWithToast, projectId, openToast];
    $[15] = dispatch;
    $[16] = handleErrorWithToast;
    $[17] = openToast;
    $[18] = projectId;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[20] !== isLoading || $[21] !== snapshots.length || $[22] !== trackEvent) {
    t7 = () => {
      if (!isLoading && snapshots.length > 0 && !hasTrackedEvent.current) {
        hasTrackedEvent.current = true;
        trackEvent({
          name: SnapshotEvents.loadedSnapshots,
          props: {
            totalSnapshots: snapshots.length
          }
        });
      }
    };
    t8 = [isLoading, snapshots.length, trackEvent];
    $[20] = isLoading;
    $[21] = snapshots.length;
    $[22] = trackEvent;
    $[23] = t7;
    $[24] = t8;
  } else {
    t7 = $[23];
    t8 = $[24];
  }
  useEffect(t7, t8);
  const shouldShowEmptyPage = snapshots.length === 0 && !isLoading;
  let t9;
  if ($[25] !== sortedFilteredSnapshots) {
    t9 = sortedFilteredSnapshots.some(isUserSnapshotAdmin);
    $[25] = sortedFilteredSnapshots;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const hasAnyAdminSnapshot = t9;
  let t10;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <BulkDeleteSnapshotsSuccessDialog />;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== buttonComponents || $[29] !== debouncedSearchText || $[30] !== hasAnyAdminSnapshot || $[31] !== isLoading || $[32] !== isScreenSmOrSmaller || $[33] !== projectId || $[34] !== setSortBy || $[35] !== shouldShowEmptyPage || $[36] !== snapshots.length || $[37] !== sortBy || $[38] !== sortedFilteredSnapshots) {
    t11 = <>{t10}{shouldShowEmptyPage ? <SnapshotsEmptyPage debouncedSearchText={debouncedSearchText} /> : <>{snapshots.length > 0 && <ProjectSnapshotsKpi />}<ColumnPreferencesProvider tableKey="project-snapshots-table" columns={[...SNAPSHOTS_TABLE_COLUMNS]} options={{
          nonConfigurableColumns: ["options"],
          defaultVisibleColumns: ["name", "accessLevel", "created"]
        }}><EntitiesToolbar toolbarEntityName="Snapshot" numberOfRows={sortedFilteredSnapshots.length} isLoading={isLoading} mobileAction={<CreateSnapshot projectId={projectId} />} sortActions={sortedFilteredSnapshots.length > 0 && <SortSelector sortOptions={SNAPSHOTS_SORT_OPTIONS} sortItem={SNAPSHOTS_SORT_BY_MAP[sortBy] ?? SNAPSHOTS_SORT_BY_MAP[DEFAULT_SNAPSHOTS_SORT_BY]} setSortItem={newSortItem => setSortBy(getSnapshotsSortBy(newSortItem))} subjectType="Snapshot" />} exportComponent={getExportComponent(sortedFilteredSnapshots.length, buttonComponents.export)} />{isScreenSmOrSmaller ? <SnapshotsMobileView snapshots={sortedFilteredSnapshots} isLoading={isLoading} sortBy={sortBy} bulkActionButtons={buttonComponents} /> : <SnapshotsTable snapshots={sortedFilteredSnapshots} isLoading={isLoading} sortBy={sortBy} setSortBy={setSortBy} bulkActionButtons={buttonComponents} shouldHideCheckbox={!hasAnyAdminSnapshot} />}</ColumnPreferencesProvider></>}</>;
    $[28] = buttonComponents;
    $[29] = debouncedSearchText;
    $[30] = hasAnyAdminSnapshot;
    $[31] = isLoading;
    $[32] = isScreenSmOrSmaller;
    $[33] = projectId;
    $[34] = setSortBy;
    $[35] = shouldShowEmptyPage;
    $[36] = snapshots.length;
    $[37] = sortBy;
    $[38] = sortedFilteredSnapshots;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  return t11;
}